class PlanningValidator {
  constructor(employees) {
    this.employees = employees.filter(emp => emp.status === 'active');
    this.ocrResults = null; // Store OCR results if available
  }

  setOcrResults(ocrResults) {
    this.ocrResults = ocrResults;
    console.log('✅ OCR results set in validator');
  }

  normalizeName(name) {
    return name
      .toUpperCase()
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[àáâãäå]/g, 'A')
      .replace(/[èéêë]/g, 'E')
      .replace(/[ìíîï]/g, 'I')
      .replace(/[òóôõö]/g, 'O')
      .replace(/[ùúûü]/g, 'U')
      .replace(/[ýÿ]/g, 'Y')
      .replace(/[ç]/g, 'C')
      .replace(/[^A-Z0-9\s]/g, '');
  }

  // Get employee display name (prefers aliases > first_name > name)
  getEmployeeDisplayName(employee) {
    if (employee.aliases && Array.isArray(employee.aliases) && employee.aliases.length > 0) {
      return employee.aliases[0];
    }
    if (employee.first_name) {
      return employee.first_name;
    }
    return employee.name;
  }

  findEmployee(planningName) {
    const normalized = this.normalizeName(planningName);

    // Priority 1: Recherche par aliases
    let found = this.employees.find(emp => {
      if (emp.aliases && Array.isArray(emp.aliases)) {
        return emp.aliases.some(alias => {
          const normalizedAlias = this.normalizeName(alias);
          return normalizedAlias === normalized ||
                 normalizedAlias.includes(normalized) ||
                 normalized.includes(normalizedAlias);
        });
      }
      return false;
    });

    if (found) return found;

    // Priority 2: Recherche par first_name
    found = this.employees.find(emp => {
      if (emp.first_name) {
        const normalizedFirstName = this.normalizeName(emp.first_name);
        return normalizedFirstName === normalized ||
               normalizedFirstName.includes(normalized) ||
               normalized.includes(normalizedFirstName);
      }
      return false;
    });

    if (found) return found;

    // Priority 3: Recherche exacte par nom de famille
    found = this.employees.find(emp =>
      this.normalizeName(emp.name) === normalized
    );

    if (found) return found;

    // Recherche partielle (prénom, nom, surnom)
    const parts = normalized.split(' ');
    found = this.employees.find(emp => {
      const empParts = this.normalizeName(emp.name).split(' ');
      return parts.some(part =>
        part.length > 2 && empParts.some(empPart =>
          empPart.includes(part) || part.includes(empPart)
        )
      );
    });

    return found || null;
  }

  extractBaseNames(text) {
    const entries = [];
    const lines = text.split('\n');

    let inBaseSection = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Détecte la section BASE
      if (line.includes('BASE') && line.includes('SEABOB')) {
        inBaseSection = true;
        continue;
      }

      // Fin de la section BASE
      if (inBaseSection && (line.includes('GLADYS') || line.includes('7H') || line === '')) {
        if (line.includes('GLADYS') || line.includes('7H')) {
          inBaseSection = false;
        }
        continue;
      }

      if (inBaseSection && line.length > 0) {
        const parts = line.split(/\s+/);
        const name = parts[0];
        const am = parts.includes('AM') ? 'BASE' : '-';
        const pm = parts.includes('PM') ? 'BASE' : parts.includes('AM') ? '-' : 'BASE';

        if (name && name.length > 1 && !name.match(/^\d/)) {
          entries.push({
            name,
            am: am,
            pm: pm,
            section: 'BASE'
          });
        }
      }
    }

    return entries;
  }

  extractTransfers(text) {
    const transfers = [];
    const lines = text.split('\n');

    // Bateaux valides
    const validBoats = ['74', '68', '54', '80', '69'];

    // Concierges/Hotels valides
    const validConcierges = ['CBI', 'BONIBARTH', 'HOTEL', 'CONCIERGE'];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const upperLine = line.toUpperCase();

      // Détecte les transferts (ligne contenant TRANSFER ou TRSF)
      if (upperLine.includes('TRANSFER') || upperLine.includes('TRSF')) {
        const transferData = {
          line: line,
          lineNumber: i + 1,
          hasFlightNumber: false,
          hasLandingTime: false,
          hasConcierge: false,
          hasVipInfo: false,
          hasBoatNumber: false,
          flightNumber: null,
          landingTime: null,
          concierge: null,
          vip: null,
          boatNumber: null
        };

        // Recherche numéro de vol (ex: AF123, BA456, etc.)
        const flightPattern = /[A-Z]{2}\d{2,4}/g;
        const flightMatch = line.match(flightPattern);
        if (flightMatch) {
          transferData.hasFlightNumber = true;
          transferData.flightNumber = flightMatch[0];
        }

        // Recherche heure d'atterrissage (ex: 14h30, 14:30, 14H30)
        const timePattern = /\d{1,2}[hH:]\d{2}/g;
        const timeMatch = line.match(timePattern);
        if (timeMatch) {
          transferData.hasLandingTime = true;
          transferData.landingTime = timeMatch[0];
        }

        // Recherche concierge/hotel
        for (const concierge of validConcierges) {
          if (upperLine.includes(concierge)) {
            transferData.hasConcierge = true;
            transferData.concierge = concierge;
            break;
          }
        }

        // Recherche info VIP
        if (upperLine.includes('VIP')) {
          transferData.hasVipInfo = true;
          transferData.vip = 'VIP';
        } else if (upperLine.includes('NO VIP') || upperLine.includes('NON VIP')) {
          transferData.hasVipInfo = true;
          transferData.vip = 'NO VIP';
        }

        // Recherche numéro de bateau
        for (const boat of validBoats) {
          if (line.includes(boat)) {
            transferData.hasBoatNumber = true;
            transferData.boatNumber = boat;
            break;
          }
        }

        transfers.push(transferData);
      }
    }

    return transfers;
  }

  extractAllNames(text) {
    const names = new Set();
    const normalizedText = this.normalizeName(text);

    // Build variations dynamically from employees database
    // Priority: aliases > first_name (no last names)
    this.employees.forEach(employee => {
      const variations = [];

      // Add aliases (highest priority)
      if (employee.aliases && Array.isArray(employee.aliases)) {
        employee.aliases.forEach(alias => {
          variations.push(alias);
        });
      }

      // Add first_name
      if (employee.first_name) {
        variations.push(employee.first_name);
      }

      // Check if any variation is found in the text
      const canonical = employee.aliases?.[0] || employee.first_name || employee.name;

      for (const variation of variations) {
        if (normalizedText.includes(this.normalizeName(variation))) {
          names.add(this.normalizeName(canonical));
          break;
        }
      }
    });

    return names;
  }

  extractAstreinteNames(text) {
    const astreinteNames = [];
    const lines = text.split('\n');

    let inAstreinteSection = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const upperLine = line.toUpperCase();

      // Détecte la section ASTREINTE
      if (upperLine.includes('ASTREINTE')) {
        inAstreinteSection = true;

        // Le nom peut être sur la même ligne après ASTREINTE
        const afterAstreinte = line.substring(line.toUpperCase().indexOf('ASTREINTE') + 9).trim();
        if (afterAstreinte.length > 0) {
          const parts = afterAstreinte.split(/\s+/);
          const name = parts[0];
          if (name && name.length > 1 && !name.match(/^\d/)) {
            astreinteNames.push(name);
          }
        }
        continue;
      }

      // Si on est dans la section ASTREINTE et on trouve un nom
      if (inAstreinteSection && line.length > 0) {
        // Fin de la section si on trouve BASE, OFF, ou autres sections
        if (upperLine.includes('BASE') || upperLine.includes('OFF') ||
            upperLine.includes('SEABOB') || upperLine.includes('JETSKI')) {
          inAstreinteSection = false;
          continue;
        }

        const parts = line.split(/\s+/);
        const name = parts[0];
        if (name && name.length > 1 && !name.match(/^\d/)) {
          astreinteNames.push(name);
          inAstreinteSection = false; // Usually only one person on astreinte
        }
      }
    }

    return astreinteNames;
  }

  extractOffNames(text) {
    const offNames = [];
    const lines = text.split('\n');

    let inOffSection = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      const upperLine = line.toUpperCase();

      // Détecte la section OFF
      if (upperLine.includes('OFF') && !upperLine.includes('OFFICE')) {
        inOffSection = true;

        // Le nom peut être sur la même ligne après OFF
        const afterOff = line.substring(line.toUpperCase().indexOf('OFF') + 3).trim();
        if (afterOff.length > 0) {
          const parts = afterOff.split(/\s+/);
          for (const part of parts) {
            if (part && part.length > 1 && !part.match(/^\d/)) {
              offNames.push(part);
            }
          }
        }
        continue;
      }

      // Si on est dans la section OFF et on trouve des noms
      if (inOffSection && line.length > 0) {
        // Fin de la section si on trouve d'autres sections
        if (upperLine.includes('BASE') || upperLine.includes('ASTREINTE') ||
            upperLine.includes('SEABOB') || upperLine.includes('JETSKI')) {
          inOffSection = false;
          continue;
        }

        const parts = line.split(/\s+/);
        for (const part of parts) {
          if (part && part.length > 1 && !part.match(/^\d/)) {
            offNames.push(part);
          }
        }
      }
    }

    return offNames;
  }

  extractCategoryTable(text) {
    // If OCR results are available, use them instead
    if (this.ocrResults && this.ocrResults.categories) {
      console.log('===== UTILISATION DES RÉSULTATS OCR/OPENAI =====');
      console.log(JSON.stringify(this.ocrResults.categories, null, 2));

      // Ensure all 7 categories exist in the results
      const normalizedCategories = {
        ASTREINTE: this.ocrResults.categories.ASTREINTE || [],
        BASE: this.ocrResults.categories.BASE || [],
        SEABOB: this.ocrResults.categories.SEABOB || [],
        JETSKI: this.ocrResults.categories.JETSKI || [],
        BUREAU: this.ocrResults.categories.BUREAU || [],
        ST_KITTS: this.ocrResults.categories.ST_KITTS || [],
        OFF: this.ocrResults.categories.OFF || []
      };

      return normalizedCategories;
    }

    // Fallback to text-based extraction
    console.log('===== EXTRACTION BASÉE SUR LE TEXTE =====');

    // Extract categories from the table at the bottom of the planning
    const categories = {
      ASTREINTE: [],
      BASE: [],
      SEABOB: [],
      JETSKI: [],
      BUREAU: [],
      ST_KITTS: [],
      OFF: []
    };

    const lines = text.split('\n');
    let headerLineIndex = -1;
    let headerLine = '';

    console.log('===== DEBUT EXTRACTION TABLEAU =====');
    console.log(`Total lignes dans le PDF: ${lines.length}`);

    // Find table header
    for (let i = 0; i < lines.length; i++) {
      const upperLine = lines[i].toUpperCase();
      if (upperLine.includes('ASTREINTE') && upperLine.includes('BASE') &&
          (upperLine.includes('SEABOB') || upperLine.includes('JETSKI') || upperLine.includes('OFF'))) {
        headerLineIndex = i;
        headerLine = lines[i];
        console.log(`Trouvé header du tableau à la ligne ${i}:`, headerLine);
        break;
      }
    }

    if (headerLineIndex === -1) {
      console.log('❌ ERREUR: Aucun header de tableau trouvé');
      // Essayons de trouver les noms manuellement dans tout le texte
      return this.extractCategoriesManually(text);
    }

    // Collect all table rows first to see what we're working with
    const tableRows = [];
    for (let i = headerLineIndex + 1; i < lines.length; i++) {
      const line = lines[i];
      const upperLine = line.toUpperCase();

      // Stop at empty line or end markers
      if (line.trim() === '' || upperLine.includes('MISSIONS') || upperLine.includes('AIDES DEPART')) {
        break;
      }

      tableRows.push(line);
    }

    console.log(`Trouvé ${tableRows.length} lignes de tableau`);
    console.log('Lignes brutes du tableau:');
    tableRows.forEach((row, idx) => console.log(`  ${idx}: "${row}"`));

    // Try different parsing strategies

    // Strategy 1: Column-based parsing (if columns are aligned)
    const upperHeader = headerLine.toUpperCase();
    const columnPositions = {
      ASTREINTE: upperHeader.indexOf('ASTREINTE'),
      BASE: upperHeader.indexOf('BASE'),
      SEABOB: upperHeader.indexOf('SEABOB'),
      JETSKI: upperHeader.indexOf('JETSKI'),
      BUREAU: upperHeader.indexOf('BUREAU'),
      ST_KITTS: upperHeader.search(/ST\s*KITTS/i),
      OFF: upperHeader.lastIndexOf('OFF')
    };

    console.log('Positions des colonnes:', columnPositions);

    // Check if we have reasonable column positions
    const validColumns = Object.entries(columnPositions).filter(([_, pos]) => pos !== -1);

    if (validColumns.length >= 3) {
      // Use column-based parsing
      console.log('Utilisation du parsing par colonnes');

      const sortedColumns = validColumns.sort((a, b) => a[1] - b[1]);
      const columnRanges = sortedColumns.map((col, index) => ({
        name: col[0],
        start: col[1],
        end: index < sortedColumns.length - 1 ? sortedColumns[index + 1][1] : 1000
      }));

      console.log('Plages de colonnes:', columnRanges);

      for (const range of columnRanges) {
        const columnTexts = [];

        for (const row of tableRows) {
          const columnText = row.substring(range.start, range.end).trim();
          if (columnText && columnText.length > 0) {
            columnTexts.push(columnText);
          }
        }

        const fullColumnText = columnTexts.join('\n');
        console.log(`\nColonne ${range.name}:\n${fullColumnText}`);

        const names = this.extractNamesFromColumnText(fullColumnText);
        console.log(`Noms extraits pour ${range.name}:`, names);

        categories[range.name].push(...names);
      }
    } else {
      // Fallback: Split by multiple spaces/tabs
      console.log('❌ Colonnes mal alignées, utilisation du parsing par espaces');

      for (const row of tableRows) {
        // Split by 2+ spaces or tabs
        const parts = row.split(/\s{2,}|\t/).map(p => p.trim()).filter(p => p.length > 0);
        console.log(`Row parts:`, parts);

        // Try to match each part to a category based on position or context
        parts.forEach((part, idx) => {
          const cleaned = this.cleanNameText(part);
          if (cleaned && cleaned.length > 1) {
            // Assign to category based on position
            if (idx === 0) categories.ASTREINTE.push(cleaned);
            else if (idx === 1) categories.BASE.push(cleaned);
            else if (idx === 2) categories.SEABOB.push(cleaned);
            else if (idx === 3) categories.JETSKI.push(cleaned);
            else if (idx === 4) categories.BUREAU.push(cleaned);
            else if (idx === 5) categories.ST_KITTS.push(cleaned);
            else if (idx === 6) categories.OFF.push(cleaned);
          }
        });
      }
    }

    // Remove duplicates and clean
    for (const category in categories) {
      categories[category] = [...new Set(categories[category])].filter(name =>
        name && name.length > 1
      );
    }

    console.log('\n===== CATEGORIES FINALES =====');
    console.log(JSON.stringify(categories, null, 2));
    console.log('===== FIN EXTRACTION =====\n');

    return categories;
  }

  extractNamesFromColumnText(text) {
    // Extract names from column text
    const names = [];
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

    for (const line of lines) {
      const cleaned = this.cleanNameText(line);
      if (cleaned && cleaned.length > 1) {
        // Check if it's not a header or keyword
        const upperCleaned = cleaned.toUpperCase();
        if (!upperCleaned.includes('ASTREINTE') &&
            !upperCleaned.includes('SEABOB') &&
            !upperCleaned.includes('JETSKI') &&
            !upperCleaned.includes('BUREAU') &&
            !upperCleaned.includes('KITTS') &&
            upperCleaned !== 'BASE' &&
            upperCleaned !== 'OFF' &&
            !upperCleaned.includes('AIDES')) {
          names.push(cleaned);
        }
      }
    }

    return names;
  }

  cleanNameText(text) {
    // Clean a name text
    let cleaned = text.trim();

    // Remove time prefixes (7H, 8H, 14H, etc.)
    cleaned = cleaned.replace(/^\d+H?\s*/i, '');

    // Remove AM/PM suffixes
    cleaned = cleaned.replace(/\s+(AM|PM)\s*$/i, '');

    // Remove "+" prefix
    cleaned = cleaned.replace(/^\+\s*/, '');

    // Remove numbers at the end
    cleaned = cleaned.replace(/\s+\d+\s*$/, '');

    return cleaned.trim();
  }

  extractCategoriesManually(text) {
    // Fallback: Extract categories by searching for keywords in the whole text
    console.log('⚠️ Utilisation de l\'extraction manuelle (fallback)');

    const categories = {
      ASTREINTE: [],
      BASE: [],
      SEABOB: [],
      JETSKI: [],
      BUREAU: [],
      ST_KITTS: [],
      OFF: []
    };

    // Use the existing text extraction methods as fallback
    const astreinteNames = this.extractAstreinteNames(text);
    const offNames = this.extractOffNames(text);
    const baseNames = this.extractBaseNames(text);

    categories.ASTREINTE = astreinteNames;
    categories.OFF = offNames;
    categories.BASE = baseNames.map(b => b.name);

    // Search for SEABOB, JETSKI mentions in text
    const lines = text.split('\n');
    for (const line of lines) {
      const upperLine = line.toUpperCase();

      if (upperLine.includes('SEABOB')) {
        // Extract names near SEABOB
        const parts = line.split(/\s+/);
        const seabobIndex = parts.findIndex(p => p.toUpperCase().includes('SEABOB'));
        if (seabobIndex > 0) {
          const name = this.cleanNameText(parts[seabobIndex - 1]);
          if (name && name.length > 2) categories.SEABOB.push(name);
        }
        if (seabobIndex < parts.length - 1) {
          const name = this.cleanNameText(parts[seabobIndex + 1]);
          if (name && name.length > 2) categories.SEABOB.push(name);
        }
      }

      if (upperLine.includes('JETSKI') || upperLine.includes('JET SKI')) {
        const parts = line.split(/\s+/);
        const jetskiIndex = parts.findIndex(p => p.toUpperCase().includes('JETSKI') || p.toUpperCase().includes('JET'));
        if (jetskiIndex > 0) {
          const name = this.cleanNameText(parts[jetskiIndex - 1]);
          if (name && name.length > 2) categories.JETSKI.push(name);
        }
        if (jetskiIndex < parts.length - 1) {
          const name = this.cleanNameText(parts[jetskiIndex + 1]);
          if (name && name.length > 2) categories.JETSKI.push(name);
        }
      }
    }

    // Remove duplicates
    for (const cat in categories) {
      categories[cat] = [...new Set(categories[cat])];
    }

    console.log('Catégories extraites manuellement:', categories);

    return categories;
  }

  extractNamesFromText(text) {
    // Extract names from text, filtering out headers and noise
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const names = [];

    for (const line of lines) {
      const upperLine = line.toUpperCase();

      // Skip header words
      if (upperLine.includes('ASTREINTE') || upperLine.includes('BASE') ||
          upperLine.includes('SEABOB') || upperLine.includes('JETSKI') ||
          upperLine.includes('BUREAU') || upperLine.includes('ST KITTS') ||
          upperLine === 'OFF' || upperLine.includes('AIDES')) {
        continue;
      }

      // Clean up and extract name
      let cleanLine = line.replace(/^\d+H?\s*/, '').trim(); // Remove time prefixes
      if (cleanLine.length > 1 && !cleanLine.match(/^\d+$/)) {
        names.push(cleanLine);
      }
    }

    return names;
  }

  extractCategoriesFromPlanning(text, planningDate) {
    const categorizedEmployees = [];

    // Extract from category table (most reliable)
    const categoryTable = this.extractCategoryTable(text);

    // Store the original BASE column entries with AM/PM info
    const baseColumnWithTimings = this.extractBaseColumnWithTimings(text);

    console.log('Base column with timings:', baseColumnWithTimings);

    // Normalize and categorize each employee
    this.employees.forEach(employee => {
      let category = null;
      let found = false;

      const displayName = this.getEmployeeDisplayName(employee);

      // Priority 1: Check ASTREINTE column
      for (const astreinteName of categoryTable.ASTREINTE) {
        if (this.employeeMatchesName(employee, astreinteName)) {
          category = 'ASTREINTE';
          found = true;
          console.log(`${displayName} (${employee.name}) -> ASTREINTE (from table)`);
          break;
        }
      }

      // Priority 2: Check OFF column in table (definitive)
      if (!found) {
        for (const offName of categoryTable.OFF) {
          if (this.employeeMatchesName(employee, offName)) {
            category = 'OFF';
            found = true;
            console.log(`${displayName} (${employee.name}) -> OFF (from table)`);
            break;
          }
        }
      }

      // Priority 3: Check SEABOB
      if (!found) {
        for (const seabobName of categoryTable.SEABOB) {
          if (this.employeeMatchesName(employee, seabobName)) {
            category = 'SEABOB';
            found = true;
            console.log(`${displayName} (${employee.name}) -> SEABOB`);
            break;
          }
        }
      }

      // Priority 4: Check JETSKI
      if (!found) {
        for (const jetskiName of categoryTable.JETSKI) {
          if (this.employeeMatchesName(employee, jetskiName)) {
            category = 'JETSKI';
            found = true;
            console.log(`${displayName} (${employee.name}) -> JETSKI`);
            break;
          }
        }
      }

      // Priority 5: Check ST_KITTS
      if (!found) {
        for (const stKittsName of categoryTable.ST_KITTS) {
          if (this.employeeMatchesName(employee, stKittsName)) {
            category = 'ST_KITTS';
            found = true;
            console.log(`${displayName} (${employee.name}) -> ST_KITTS`);
            break;
          }
        }
      }

      // Priority 6: Check BUREAU
      if (!found) {
        for (const bureauName of categoryTable.BUREAU) {
          if (this.employeeMatchesName(employee, bureauName)) {
            category = 'BUREAU';
            found = true;
            console.log(`${displayName} (${employee.name}) -> BUREAU`);
            break;
          }
        }
      }

      // Priority 7: Check BASE column
      if (!found) {
        for (const baseName of categoryTable.BASE) {
          if (this.employeeMatchesName(employee, baseName)) {
            category = 'BASE';
            found = true;
            console.log(`${displayName} (${employee.name}) -> BASE`);
            break;
          }
        }
      }

      // If employee is not found anywhere in table, mark as OFF
      if (!found) {
        category = 'OFF';
        console.log(`${displayName} (${employee.name}) -> OFF (not found in planning)`);
      }

      categorizedEmployees.push({
        employee_id: employee.id,
        employee_name: displayName, // Use display name (alias or first_name)
        employee_full_name: employee.name, // Keep full name for reference
        employee_role: employee.role,
        category: category,
        off_date: planningDate
      });
    });

    return categorizedEmployees;
  }

  extractBaseColumnWithTimings(text) {
    // Extract BASE column entries with their AM/PM info
    const results = [];
    const lines = text.split('\n');
    let headerLineIndex = -1;
    let baseColStart = -1;
    let baseColEnd = -1;

    // Find table header and BASE column position
    for (let i = 0; i < lines.length; i++) {
      const upperLine = lines[i].toUpperCase();
      if (upperLine.includes('ASTREINTE') && upperLine.includes('BASE') &&
          (upperLine.includes('SEABOB') || upperLine.includes('JETSKI') || upperLine.includes('OFF'))) {
        headerLineIndex = i;
        const headerLine = lines[i];
        const upperHeader = headerLine.toUpperCase();

        baseColStart = upperHeader.indexOf('BASE');

        // Find where BASE column ends (next column starts)
        const seabobPos = upperHeader.indexOf('SEABOB');
        const jetskiPos = upperHeader.indexOf('JETSKI');
        baseColEnd = Math.min(
          seabobPos !== -1 ? seabobPos : 1000,
          jetskiPos !== -1 ? jetskiPos : 1000
        );

        break;
      }
    }

    if (headerLineIndex === -1 || baseColStart === -1) {
      return results;
    }

    // Extract BASE column entries
    for (let i = headerLineIndex + 1; i < lines.length; i++) {
      const line = lines[i];
      const upperLine = line.toUpperCase();

      // Stop at empty line or end markers
      if (line.trim() === '' || upperLine.includes('MISSIONS') || upperLine.includes('AIDES DEPART')) {
        break;
      }

      // Extract text from BASE column
      const baseText = line.substring(baseColStart, baseColEnd).trim();
      if (baseText && baseText.length > 0) {
        const upperBase = baseText.toUpperCase();

        // Check for AM/PM
        let period = null;
        let name = baseText;

        if (upperBase.includes(' AM')) {
          period = 'AM';
          name = baseText.replace(/\s+AM\s*$/i, '').trim();
        } else if (upperBase.includes(' PM')) {
          period = 'PM';
          name = baseText.replace(/\s+PM\s*$/i, '').trim();
        }

        if (name.length > 1) {
          results.push({ name, period });
        }
      }
    }

    return results;
  }

  employeeMatchesName(employee, planningName) {
    const normalizedPlanning = this.normalizeName(planningName);

    // Priority 1: Check aliases first (most commonly used names)
    if (employee.aliases && Array.isArray(employee.aliases)) {
      for (const alias of employee.aliases) {
        const normalizedAlias = this.normalizeName(alias);
        if (normalizedAlias === normalizedPlanning ||
            normalizedPlanning.includes(normalizedAlias) ||
            normalizedAlias.includes(normalizedPlanning)) {
          return true;
        }
      }
    }

    // Priority 2: Check first name
    if (employee.first_name) {
      const normalizedFirstName = this.normalizeName(employee.first_name);
      if (normalizedFirstName === normalizedPlanning ||
          normalizedPlanning.includes(normalizedFirstName) ||
          normalizedFirstName.includes(normalizedPlanning)) {
        return true;
      }
    }

    // Priority 3: Check last name (fallback)
    const normalizedEmployee = this.normalizeName(employee.name);
    if (normalizedEmployee === normalizedPlanning) {
      return true;
    }

    // Partial match (check if any part matches)
    const empParts = normalizedEmployee.split(' ').filter(part => part.length >= 3);
    const planningParts = normalizedPlanning.split(' ').filter(part => part.length >= 3);

    for (const empPart of empParts) {
      for (const planPart of planningParts) {
        if (empPart === planPart || empPart.includes(planPart) || planPart.includes(empPart)) {
          return true;
        }
      }
    }

    return false;
  }

  validateRules(entries) {
    const errors = [];

    entries.forEach(entry => {
      const { name, am, pm } = entry;

      // Règle 1: Transfer PM ou OFF PM nécessite BASE AM
      if (pm === '-' || pm.includes('TRANSFER') || pm.includes('TRSF')) {
        if (am === '-' || !am.includes('BASE')) {
          errors.push({
            type: 'invalid_sequence',
            severity: 'error',
            employee: name,
            message: `${name} a un transfer/off PM mais n'est pas en BASE AM`,
            suggestion: 'Ajouter BASE AM'
          });
        }
      }

      // Règle 2: HALF DAY PM nécessite BASE AM
      if (pm.includes('PM') && am === '-') {
        errors.push({
          type: 'invalid_sequence',
          severity: 'error',
          employee: name,
          message: `${name} travaille PM mais n'est pas en BASE AM`,
          suggestion: 'Ajouter BASE AM'
        });
      }

      // Règle 3: Vérifier si l'employé existe
      const employee = this.findEmployee(name);
      if (!employee) {
        errors.push({
          type: 'name_mismatch',
          severity: 'warning',
          employee: name,
          message: `${name} n'est pas trouvé dans la base de données`,
          suggestion: 'Vérifier l\'orthographe du nom'
        });
      }
    });

    return errors;
  }

  validateTransfers(transfers) {
    const errors = [];

    transfers.forEach((transfer, index) => {
      const missingInfo = [];

      if (!transfer.hasFlightNumber) {
        missingInfo.push('numéro de vol');
      }

      if (!transfer.hasLandingTime) {
        missingInfo.push('heure d\'atterrissage');
      }

      if (!transfer.hasConcierge) {
        missingInfo.push('concierge/hôtel (CBI, BONIBARTH, etc.)');
      }

      if (!transfer.hasVipInfo) {
        missingInfo.push('info VIP (VIP ou NO VIP)');
      }

      if (!transfer.hasBoatNumber) {
        missingInfo.push('numéro de bateau (74, 68, 54, 80, 69)');
      }

      if (missingInfo.length > 0) {
        errors.push({
          type: 'incomplete_transfer',
          severity: 'error',
          transfer: transfer.line,
          lineNumber: transfer.lineNumber,
          message: `Transfer ligne ${transfer.lineNumber} incomplet`,
          missingInfo: missingInfo,
          suggestion: `Ajouter: ${missingInfo.join(', ')}`
        });
      }
    });

    return errors;
  }

  validate(pdfText) {
    const baseEntries = this.extractBaseNames(pdfText);
    const allPresentNames = this.extractAllNames(pdfText);
    const transfers = this.extractTransfers(pdfText);

    const ruleErrors = this.validateRules(baseEntries);
    const transferErrors = this.validateTransfers(transfers);

    const errors = [...ruleErrors.filter(e => e.severity === 'error'), ...transferErrors];
    const warnings = ruleErrors.filter(e => e.severity === 'warning');

    const presentEmployees = [];
    const missingEmployees = [];

    this.employees.forEach(employee => {
      let found = false;

      for (const presentName of allPresentNames) {
        const displayName = this.getEmployeeDisplayName(employee);

        // Priority 1: Check aliases
        if (employee.aliases && Array.isArray(employee.aliases)) {
          const aliasMatch = employee.aliases.some(alias => {
            const normalizedAlias = this.normalizeName(alias);
            return presentName === normalizedAlias ||
                   presentName.includes(normalizedAlias) ||
                   normalizedAlias.includes(presentName);
          });

          if (aliasMatch) {
            found = true;
            presentEmployees.push(displayName);
            break;
          }
        }

        // Priority 2: Check first_name
        if (employee.first_name) {
          const normalizedFirstName = this.normalizeName(employee.first_name);
          if (presentName === normalizedFirstName ||
              presentName.includes(normalizedFirstName) ||
              normalizedFirstName.includes(presentName)) {
            found = true;
            presentEmployees.push(displayName);
            break;
          }
        }

        // Priority 3: Check last name (fallback - only if no alias or first_name)
        if (!employee.aliases?.length && !employee.first_name) {
          const normalized = this.normalizeName(employee.name);
          const empParts = normalized.split(' ').filter(part => part.length > 0);

          const matches = empParts.some(part => {
            if (part.length < 3) return false;
            return presentName === part ||
                   presentName.includes(part) ||
                   part.includes(presentName);
          });

          if (matches) {
            found = true;
            presentEmployees.push(displayName);
            break;
          }
        }
      }

      if (!found) {
        const displayName = this.getEmployeeDisplayName(employee);
        missingEmployees.push(displayName);
        errors.push({
          type: 'missing_employee',
          severity: 'error',
          employee: displayName,
          message: `${displayName} (${employee.role}) n'apparaît pas dans le planning`,
          suggestion: 'Vérifier si absent ou oublié'
        });
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      transfers: {
        total: transfers.length,
        complete: transfers.filter(t =>
          t.hasFlightNumber &&
          t.hasLandingTime &&
          t.hasConcierge &&
          t.hasVipInfo &&
          t.hasBoatNumber
        ).length,
        incomplete: transfers.filter(t =>
          !t.hasFlightNumber ||
          !t.hasLandingTime ||
          !t.hasConcierge ||
          !t.hasVipInfo ||
          !t.hasBoatNumber
        ).length,
        details: transfers
      },
      summary: {
        totalEmployees: this.employees.length,
        presentEmployees,
        missingEmployees,
        issuesCount: errors.length + warnings.length
      }
    };
  }
}

export default PlanningValidator;
